import type {
  IDepositTerminationApplicationForm,
  TDepositTerminationApplicationRequest,
} from "@/modules/application/common/types";
import {
  useCreateDepositTerminationApplicationMutation,
  useGetDepositTerminationApplicationQuery,
  useUpdateDepositTerminationApplicationMutation
} from "@/modules/application/services";
import type { IDeposit } from "@/modules/deposit/common/types";
import { useGetDepositsQuery } from "@/modules/deposit/services";
import { CURRENCY, INITIAL_PAGE_SIZE, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { InfoContainer, Select } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { useHasFormChanges } from "@/shared/hooks";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { ApplicationTermsAndConditionsLink } from "../ApplicationTermsAndConditionsLink";

export const DepositTerminationApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, reset, watch } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: depositsList = [],
    isLoading: isGetDepositsLoading
  } = useGetDepositsQuery({ pageSize: INITIAL_PAGE_SIZE });

  const {
    data: depositTerminationApplication,
    isLoading: isGetApplicationLoading,
  } = useGetDepositTerminationApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createDepositTerminationApplicationApi,
    {
      isLoading: isCreateLoading
    }] = useCreateDepositTerminationApplicationMutation();

  const [
    updateDepositTerminationApplicationApi,
    {
      isLoading: isUpdateLoading
    }
  ] = useUpdateDepositTerminationApplicationMutation();

  const depositTerminationApplicationAction = useCallback((values: IDepositTerminationApplicationForm) => {
    const requestData: TDepositTerminationApplicationRequest = {
      ...values,
    };
    return isEdit ? updateDepositTerminationApplicationApi({
        data: requestData,
        id: `${depositTerminationApplication?.id}`,
      }).unwrap() :
      createDepositTerminationApplicationApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [depositTerminationApplication]);

  const depositsOptions = useMemo(() =>
    depositsList.map((deposit: IDeposit) => ({
      label: `${deposit.contractNumber} - ${deposit.initialAmount} ${CURRENCY[deposit.currency]?.symbol}`,
      value: deposit.contractNumber,
    })), [depositsList]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      purpose: depositTerminationApplication?.purpose,
      depositNumber: depositTerminationApplication?.depositNumber,
      applicationNumber: isEdit ?
        depositTerminationApplication?.applicationNumber : values.applicationNumber,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    purpose: values.purpose,
    depositNumber: values.depositNumber,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!depositTerminationApplication?.id) {
      setDefaultValues();
    }
  }, [depositTerminationApplication]);

  return (
    <ApplicationLayout
      title={t("application.depositTermination.title")}
      page="Deposit termination application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={depositTerminationApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="depositNumber"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    options={depositsOptions}
                    isDisabled={isGetDepositsLoading}
                    label={t("application.depositTermination.depositNumber")}
                    placeholder={t("application.depositTermination.chooseDeposit")}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-32"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <InfoContainer
                className="mb-32"
                content={t("application.depositTermination.info")}
              />
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
